// src/cockpit4/components/MentionRankingPanel.tsx
// ============================================
// 品牌监测 → 提及率和排名（截图 2 + 截图 3）
//
// 截图 2 区域：
//   · 数据概览：对话次数 / 提及对话次数 / 提及率 / Top1 / Top3 / 提及次数 / 平均排名
//   · 提及率热力图（问题 × 平台）
//   · 提及率情况：整体提及率图 + 提及率趋势
//
// 截图 3 区域：
//   · 平均提及排名柱状图 + 平均提及排名趋势折线
//   · 品牌提及次数柱状图 + 提及次数趋势折线
//   · 品牌提及记录表格（AI问题×平台×类型×热度×监测天数×提及天数×提及次数×平均排名×提及品牌）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Trophy, BarChart3, Plus, Calendar, ChevronDown, Info,
  ListTree, LineChart as LineIcon, Settings2, ArrowUpDown,
} from "lucide-react";
import {
  ResponsiveContainer,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  AreaChart, Area, BarChart, Bar, LineChart, Line,
} from "recharts";
import type { BrandMonitorData } from "../../runtime/GrowthFactory/brandMonitorData";
import {
  MONITOR_QUESTIONS,
  ALL_MONITOR_PLATFORMS,
} from "../../runtime/GrowthFactory/brandMonitorData";

interface Props {
  data: BrandMonitorData;
  brand: string;
  onAddBrand: () => void;
  platforms: string[];
  onPlatformsChange: (keys: string[]) => void;
  questions: { id: string; label: string }[];
  questionsFilter: string[];
  onQuestionsChange: (ids: string[]) => void;
  dateStart: string;
  dateEnd: string;
  onDateChange: (s: string, e: string) => void;
}

/** 热力图（recharts 无原生，用表格+CSS实现，与截图紫蓝系一致） */
function HeatmapGrid({ data }: { data: BrandMonitorData }) {
  const platformKeys = ALL_MONITOR_PLATFORMS.map((p) => p.key);
  const qids = MONITOR_QUESTIONS.map((q) => q.id);
  const heat: Record<string, Record<string, number>> = {};
  qids.forEach((q) => { heat[q] = {}; platformKeys.forEach((p) => (heat[q][p] = 0)); });
  data.heatmap.forEach((h) => {
    if (heat[h.questionId]) heat[h.questionId][h.platform] = h.rate;
  });
  const cell = (v: number) => {
    if (v <= 0) return { bg: "rgba(255,255,255,0.01)", text: "未提及", color: "#64748b" };
    const a = Math.min(1, v);
    return {
      bg: `rgba(139, 92, 246, ${0.15 + a * 0.7})`,
      text: `${Math.round(a * 100)}%`,
      color: a > 0.4 ? "#fff" : "#e9d5ff",
    };
  };
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-[10px] border-collapse">
        <thead>
          <tr>
            <th className="text-left p-2 font-mono text-gray-500 font-normal min-w-[200px] bg-black/20">
              问题 \ 平台
            </th>
            {ALL_MONITOR_PLATFORMS.map((p) => (
              <th key={p.key} className="p-2 text-center bg-black/20">
                <div className="flex items-center justify-center gap-1">
                  <span className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] text-white font-bold"
                    style={{ background: p.color }}>{p.short}</span>
                  <span className="text-white">{p.name}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {MONITOR_QUESTIONS.map((q) => (
            <tr key={q.id}>
              <td className="p-2 font-mono text-gray-300 bg-black/10 align-top">
                <div className="max-w-[280px] truncate" title={q.q}>
                  ▸ {q.q}
                </div>
                <div className="text-[9px] text-gray-600 mt-0.5">
                  {q.type} · 热度 {q.heat}
                </div>
              </td>
              {ALL_MONITOR_PLATFORMS.map((p) => {
                const c = cell(heat[q.id]?.[p.key] || 0);
                return (
                  <td key={p.key} className="p-1.5 text-center">
                    <div
                      className="rounded py-2.5 text-[10px] font-mono"
                      style={{ background: c.bg, color: c.color }}
                    >
                      {c.text}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {data.heatmap.length === 0 && (
        <div className="py-16 text-center">
          <div className="text-gray-500 text-[12px] mt-3">提及率热力图</div>
          <div className="text-[10px] text-gray-600 mt-1">暂无监测数据</div>
        </div>
      )}
    </div>
  );
}

export default function MentionRankingPanel(props: Props) {
  const {
    data, brand, onAddBrand,
    platforms, onPlatformsChange,
    questions, questionsFilter, onQuestionsChange,
    dateStart, dateEnd, onDateChange,
  } = props;
  const [pOpen, setPOpen] = useState(false);
  const [qOpen, setQOpen] = useState(false);
  const kpi = data.kpi;

  const ratePieData = useMemo(() => {
    const mentioned = kpi.mentionDialogs;
    const notMentioned = Math.max(0, kpi.dialogs - kpi.mentionDialogs);
    const total = mentioned + notMentioned;
    return [
      { name: "已提及", value: mentioned, pct: total ? Math.round((mentioned / total) * 100) : 0 },
      { name: "未提及", value: notMentioned, pct: total ? Math.round((notMentioned / total) * 100) : 0 },
    ];
  }, [kpi]);

  return (
    <div className="flex flex-col gap-3 p-4">
      {/* 顶部标题 */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Trophy size={15} className="text-violet-400" />
          <span className="text-[13px] font-semibold text-white">品牌提及率和排名</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={onAddBrand}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px]
                       bg-white/[0.03] border border-white/[0.08] text-gray-400
                       hover:text-white hover:border-violet-500/40"
          >
            <Plus size={12} /> 增加品牌监测
          </button>
          <div className="relative">
            <button onClick={() => { setPOpen((o) => !o); setQOpen(false); }}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] bg-black/30 border border-white/[0.08] text-white">
              {`全部${platforms.length}个平台`} <ChevronDown size={12} />
            </button>
            {pOpen && (
              <div className="absolute top-full mt-1 left-0 z-20 bg-[#0e1220] border border-white/[0.1] rounded-md p-2 w-48 shadow-2xl">
                {ALL_MONITOR_PLATFORMS.map((p) => {
                  const on = platforms.includes(p.key);
                  return (
                    <button key={p.key}
                      onClick={() => onPlatformsChange(on ? platforms.filter((x) => x !== p.key) : [...platforms, p.key])}
                      className={`block w-full text-left px-2 py-1 rounded text-[10px] ${on ? "text-white bg-white/10" : "text-gray-400"}`}>
                      <span className="w-2 h-2 inline-block rounded-full mr-1" style={{ background: p.color }} />
                      {p.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="relative">
            <button onClick={() => { setQOpen((o) => !o); setPOpen(false); }}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] bg-black/30 border border-white/[0.08] text-white">
              {`全部${questionsFilter.length}个问题`} <ChevronDown size={12} />
            </button>
            {qOpen && (
              <div className="absolute top-full mt-1 left-0 z-20 bg-[#0e1220] border border-white/[0.1] rounded-md p-2 w-80 shadow-2xl max-h-60 overflow-auto">
                {questions.map((q) => {
                  const on = questionsFilter.includes(q.id);
                  return (
                    <button key={q.id}
                      onClick={() => onQuestionsChange(on ? questionsFilter.filter((x) => x !== q.id) : [...questionsFilter, q.id])}
                      className={`block w-full text-left px-2 py-1 rounded text-[10px] ${on ? "text-white bg-white/10" : "text-gray-400"}`}>
                      ▸ {q.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex items-center gap-1 px-2 py-1.5 rounded-md text-[11px] bg-black/30 border border-white/[0.08] text-gray-300">
            <Calendar size={12} />
            <input type="date" value={dateStart} onChange={(e) => onDateChange(e.target.value, dateEnd)}
              className="bg-transparent outline-none w-[88px] text-white" />
            <span className="text-gray-500">至</span>
            <input type="date" value={dateEnd} onChange={(e) => onDateChange(dateStart, e.target.value)}
              className="bg-transparent outline-none w-[88px] text-white" />
          </div>
        </div>
      </div>

      {/* 数据概览 */}
      <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-2.5">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-1 h-3 rounded bg-violet-500" />
          <span className="text-[11px] font-semibold text-white">数据概览</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
          {[
            { label: "对话次数", v: `${kpi.dialogs}`, color: "text-cyan-400", suffix: "次" },
            { label: "提及对话次数", v: `${kpi.mentionDialogs}`, color: "text-emerald-400", suffix: "次" },
            { label: "提及率", v: `${Math.round(kpi.mentionRate * 100)}%`, color: "text-blue-400", tip: "提及对话/对话总数" },
            { label: "Top1(首位)提及率", v: `${Math.round(kpi.top1Rate * 100)}%`, color: "text-violet-400", tip: "品牌首现排名=1的回答占比" },
            { label: "Top3(首屏)提及率", v: `${Math.round(kpi.top3Rate * 100)}%`, color: "text-amber-400", tip: "排名≤3的回答占比" },
            { label: "品牌提及次数", v: `${kpi.mentions}`, color: "text-pink-400", suffix: "次", tip: "多次提及累计" },
            { label: "平均提及排名", v: kpi.avgRank < 0 ? "--" : `${kpi.avgRank.toFixed(1)}`, color: "text-fuchsia-400", suffix: "名", tip: "越小越好，1=首位" },
          ].map((x) => (
            <div key={x.label} className="rounded-md bg-white/[0.02] border border-white/[0.05] p-2">
              <div className={`text-[20px] font-bold tracking-tight ${x.color}`}>
                {x.v}<small className="text-[10px] text-gray-500 font-mono ml-0.5">{x.suffix || ""}</small>
              </div>
              <div className="text-[10px] text-gray-300 mt-0.5 flex items-center gap-1">
                {x.label}
                {x.tip && <Info size={9} className="text-gray-600" />}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 提及率热力图（截图2核心图） */}
      <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-1 h-4 rounded bg-violet-500" />
          <span className="text-[12px] font-semibold text-white">提及率热力图（问题 × 平台）</span>
        </div>
        <HeatmapGrid data={data} />
      </div>

      {/* 提及率情况（截图2下方）：品牌提及率饼/柱 + 提及率趋势 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <BarChart3 size={12} className="text-violet-400" />
              <span className="text-[11px] font-semibold text-white">品牌提及率</span>
            </div>
            <select defaultValue="overall" className="text-[10px] bg-black/30 border border-white/[0.08] text-gray-300 rounded px-2 py-1 outline-none">
              <option value="overall">整体提及率</option>
              <option value="top1">Top1提及率</option>
              <option value="top3">Top3提及率</option>
            </select>
          </div>
          {data.mentionRateTrend.length === 0 ? (
            <div className="py-16 text-center text-[11px] text-gray-500">暂无数据</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ratePieData} margin={{ left: 10, right: 20, top: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" />
                  <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" />
                  <Tooltip contentStyle={{ background: "#0e1220", border: "1px solid rgba(255,255,255,0.08)", fontSize: 11 }} />
                  <Legend />
                  <Bar dataKey="pct" fill="#8B5CF6" name="占比（%）" radius={[6, 6, 0, 0]} label={{ position: "top", fontSize: 10, fill: "#c4b5fd" }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <LineIcon size={12} className="text-cyan-400" />
              <span className="text-[11px] font-semibold text-white">提及率趋势</span>
            </div>
            <div className="flex items-center gap-1">
              <button className="px-2 py-1 rounded text-[10px] bg-violet-500/15 text-violet-300 border border-violet-500/30">查看全部</button>
              <button className="px-2 py-1 rounded text-[10px] bg-white/[0.03] border border-white/[0.08] text-gray-400">当前品牌</button>
            </div>
          </div>
          {data.mentionRateTrend.length === 0 ? (
            <div className="py-16 text-center text-[11px] text-gray-500">暂无数据</div>
          ) : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.mentionRateTrend} margin={{ left: 5, right: 20, top: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="grad-overall" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="grad-top1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22D3EE" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#22D3EE" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="grad-top3" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" />
                  <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" tickFormatter={(v) => `${Math.round(v * 100)}%`} />
                  <Tooltip contentStyle={{ background: "#0e1220", border: "1px solid rgba(255,255,255,0.08)", fontSize: 11 }} formatter={(v: any) => `${Math.round((Number(v) || 0) * 100)}%`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="overall" name="整体提及率" stroke="#8B5CF6" fill="url(#grad-overall)" strokeWidth={2} />
                  <Area type="monotone" dataKey="top1" name="Top1率" stroke="#22D3EE" fill="url(#grad-top1)" strokeWidth={2} />
                  <Area type="monotone" dataKey="top3" name="Top3率" stroke="#F59E0B" fill="url(#grad-top3)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* 截图 3：平均提及排名 × 2  + 品牌提及次数 × 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Trophy size={12} className="text-amber-400" />
              <span className="text-[11px] font-semibold text-white">平均提及排名</span>
            </div>
            <select className="text-[10px] bg-black/30 border border-white/[0.08] text-gray-300 rounded px-2 py-1 outline-none"
              defaultValue="top">
              <option value="top">提及率Top20品牌</option>
            </select>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.rankRadar.slice(0, 10)} margin={{ left: 5, right: 20, top: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="brand" tick={{ fontSize: 10, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" />
                <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" />
                <Tooltip contentStyle={{ background: "#0e1220", border: "1px solid rgba(255,255,255,0.08)", fontSize: 11 }} />
                <Bar dataKey="value" fill="#F59E0B" name="提及率表现" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <LineIcon size={12} className="text-cyan-400" />
              <span className="text-[11px] font-semibold text-white">平均提及排名趋势</span>
            </div>
            <div className="flex items-center gap-1">
              <button className="px-2 py-1 rounded text-[10px] bg-violet-500/15 text-violet-300 border border-violet-500/30">查看全部</button>
              <button className="px-2 py-1 rounded text-[10px] bg-white/[0.03] border border-white/[0.08] text-gray-400">当前品牌</button>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.avgRankTrend} margin={{ left: 5, right: 20, top: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" />
                <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" />
                <Tooltip contentStyle={{ background: "#0e1220", border: "1px solid rgba(255,255,255,0.08)", fontSize: 11 }} />
                <Line type="monotone" dataKey="rank" stroke="#22D3EE" strokeWidth={2.2}
                  dot={{ r: 3, fill: "#22D3EE" }} name="平均排名" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <ListTree size={12} className="text-emerald-400" />
              <span className="text-[11px] font-semibold text-white">品牌提及次数</span>
            </div>
            <select className="text-[10px] bg-black/30 border border-white/[0.08] text-gray-300 rounded px-2 py-1 outline-none" defaultValue="all">
              <option value="all">全部提及次数</option>
            </select>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.rankRadar.slice(0, 10).map((r) => ({ ...r, value: r.value }))}
                margin={{ left: 5, right: 20, top: 10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="brand" tick={{ fontSize: 10, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" />
                <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" />
                <Tooltip contentStyle={{ background: "#0e1220", border: "1px solid rgba(255,255,255,0.08)", fontSize: 11 }} />
                <Bar dataKey="value" fill="#10B981" name="提及次数" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <LineIcon size={12} className="text-pink-400" />
              <span className="text-[11px] font-semibold text-white">提及次数趋势</span>
            </div>
            <div className="flex items-center gap-1">
              <button className="px-2 py-1 rounded text-[10px] bg-violet-500/15 text-violet-300 border border-violet-500/30">查看全部</button>
              <button className="px-2 py-1 rounded text-[10px] bg-white/[0.03] border border-white/[0.08] text-gray-400">当前品牌</button>
            </div>
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.mentionCountTrend} margin={{ left: 5, right: 20, top: 10, bottom: 0 }}>
                <defs>
                  <linearGradient id="grad-count" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#EC4899" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#EC4899" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" />
                <YAxis tick={{ fontSize: 10, fill: "#94A3B8" }} stroke="rgba(255,255,255,0.08)" />
                <Tooltip contentStyle={{ background: "#0e1220", border: "1px solid rgba(255,255,255,0.08)", fontSize: 11 }} />
                <Area type="monotone" dataKey="count" name="提及次数" stroke="#EC4899" fill="url(#grad-count)" strokeWidth={2.2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* 品牌提及记录表格（截图 3 底部） */}
      <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="w-1 h-4 rounded bg-violet-500" />
            <span className="text-[12px] font-semibold text-white">品牌提及记录</span>
          </div>
          <button className="flex items-center gap-1 px-2 py-1 rounded text-[10px]
                         bg-violet-500/10 text-violet-300 border border-violet-500/30 hover:bg-violet-500/20">
            <Settings2 size={11} /> 列表配置
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-[10.5px] border-collapse">
            <thead className="bg-black/30">
              <tr className="text-gray-400 font-mono">
                <th className="text-left p-2 min-w-[200px]"><span className="inline-flex items-center gap-1">AI问题 <ArrowUpDown size={10} /></span></th>
                <th className="text-left p-2 min-w-[120px]"><span className="inline-flex items-center gap-1">对话平台 <ArrowUpDown size={10} /></span></th>
                <th className="text-left p-2 min-w-[90px]">问题类型</th>
                <th className="text-left p-2 min-w-[70px]"><span className="inline-flex items-center gap-1">热度值 <Info size={9} /></span></th>
                <th className="text-right p-2 min-w-[80px]"><span className="inline-flex items-center gap-1">监测天数 <ArrowUpDown size={10} /></span></th>
                <th className="text-right p-2 min-w-[80px]"><span className="inline-flex items-center gap-1">提及天数 <ArrowUpDown size={10} /></span></th>
                <th className="text-right p-2 min-w-[80px]"><span className="inline-flex items-center gap-1">提及次数 <ArrowUpDown size={10} /></span></th>
                <th className="text-right p-2 min-w-[90px]"><span className="inline-flex items-center gap-1">平均提及排名 <ArrowUpDown size={10} /></span></th>
                <th className="text-left p-2 min-w-[100px]">提及品牌</th>
                <th className="text-center p-2 min-w-[70px]">操作</th>
              </tr>
            </thead>
            <tbody>
              {data.mentionRecords.length === 0 ? (
                <tr><td colSpan={10} className="text-center text-[11px] text-gray-500 py-8">暂无记录：请先运行一次「立即监测」任务</td></tr>
              ) : data.mentionRecords.map((r, i) => (
                <tr key={r.qid} className={i % 2 ? "bg-white/[0.01]" : ""}>
                  <td className="p-2 text-white font-mono max-w-[240px] truncate" title={r.question}>▸ {r.question}</td>
                  <td className="p-2 text-gray-300">{r.platform || "-"}</td>
                  <td className="p-2 text-cyan-400">{r.type}</td>
                  <td className="p-2 text-amber-400 text-right">{r.heat}</td>
                  <td className="p-2 text-gray-300 text-right">{r.monitorDays} 天</td>
                  <td className="p-2 text-emerald-400 text-right">{r.mentionDays} 天</td>
                  <td className="p-2 text-violet-400 text-right font-semibold">{r.mentions}</td>
                  <td className="p-2 text-right">{r.avgRank < 0 ? "-" : <span className={r.avgRank <= 3 ? "text-emerald-400" : "text-gray-300"}>第 {r.avgRank} 名</span>}</td>
                  <td className="p-2 text-pink-400">{r.brandName}</td>
                  <td className="p-2 text-center">
                    <button className="text-[10px] text-cyan-400 hover:text-cyan-300">详情</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
